import { ReactNode, forwardRef } from 'react';

import styled from 'styled-components';

import { IconHoverContainer } from '@components/icon-hover-container/icon-hover-container';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { Transient } from '@utils/types';

export const FANCY_CONTAINER_CLASS_NAME = 'icon-fancy-container';

export type IconContainerVariant =
  | 'gray'
  | 'purple'
  | 'indigo'
  | 'green'
  | 'blue'
  | 'orange'
  | 'red'
  | 'teal';

export type IconFancyContainerSize = 'sm' | 'lg';

const SIZE_TO_PX: Record<IconFancyContainerSize, number> = {
  sm: 24,
  lg: 40,
};

const VARIANT_TO_COLOR: Record<IconContainerVariant, { backgroundColor: string; color: string }> = {
  gray: {
    backgroundColor: theme.color.uiTrans5,
    color: theme.color.elementPurple50,
  },
  purple: {
    backgroundColor: theme.color.uiTrans5,
    color: theme.color.elementPurple80,
  },
  indigo: {
    backgroundColor: theme.color.indigoTrans10,
    color: theme.color.elementIndigo100,
  },
  green: {
    backgroundColor: theme.color.greenTrans10,
    color: theme.color.elementGreen200,
  },
  blue: {
    backgroundColor: theme.color.blueTrans10,
    color: theme.color.elementBlue100,
  },
  orange: {
    backgroundColor: theme.color.orangeTrans10,
    color: theme.color.elementOrange100,
  },
  red: {
    backgroundColor: theme.color.redTrans10,
    color: theme.color.elementRed100,
  },
  teal: {
    backgroundColor: theme.color.tealTrans10,
    color: theme.color.elementTeal200,
  },
};

export type IconFancyContainerProps = {
  icon: ReactNode;
  hoverIcon?: ReactNode;
  variant?: IconContainerVariant;
  size?: IconFancyContainerSize;
  isClickable?: boolean;
  hasBorder?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

export const IconFancyContainer = forwardRef<HTMLDivElement, IconFancyContainerProps>(
  (
    {
      icon,
      hoverIcon,
      variant = 'purple',
      size = 'lg',
      isClickable = false,
      hasBorder = false,
      ...rest
    },
    ref
  ) => {
    return (
      <StyledContainer
        className={FANCY_CONTAINER_CLASS_NAME}
        ref={ref}
        data-variant={variant}
        $size={size}
        $isClickable={isClickable}
        $hasBorder={hasBorder}
        $variant={variant}
        {...rest}
      >
        {hoverIcon ? <IconHoverContainer hoverIcon={hoverIcon} icon={icon} /> : icon}
      </StyledContainer>
    );
  }
);

const StyledContainer = styled.div<
  Transient<IconFancyContainerProps, 'size' | 'isClickable' | 'hasBorder' | 'variant'>
>((p) => {
  const { backgroundColor, color } = VARIANT_TO_COLOR[p.$variant ?? 'gray'];
  const sizeInPx = SIZE_TO_PX[p.$size ?? 'lg'];
  return {
    height: pxToRem(sizeInPx),
    width: pxToRem(sizeInPx),

    display: 'flex',
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    padding: pxToRem(8),
    borderRadius: pxToRem(4),
    backgroundColor,
    color,
    ...(p.$hasBorder && {
      border: `${pxToRem(1)} solid ${theme.color.border10}`,
    }),
  };
});
